
import Vue from 'vue';
import { mapState } from 'vuex';
import { ROUTER } from '@/router/constants';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import {
	AGENT_CODE_PATTERN,
	agentLinkFailure,
	AgentLinkFailure,
	AgentLinkRequest,
	decideAgentLink,
	FEEDBACK_AGENTS_API_BASE_URL,
	fetchAgentLink,
	normalizeAgentCode,
} from '@/utils/feedbackAgentsApi';

type State = 'loading' | 'enter' | 'ready' | 'deciding' | 'allowed' | 'denied' | 'error';

// "Connect a feedback agent": where a RunDiffusion developer approves their agent's sign-in
// (`npm run feedback -- login` opens it with the code filled in). Allowing it gives the agent its
// own token, which only the feedback API accepts (restServer apiv2/agent/, ADR 0041); the
// browser's own sign-in never leaves it.
export default Vue.extend({
	name: 'AdminFeedbackConnect',
	metaInfo: {
		title: 'Connect a feedback agent | RunDiffusion',
	},
	data () {
		return {
			ROUTER,
			state: 'loading' as State,
			link: null as AgentLinkRequest | null,
			typed: '',
			formValid: false,
			deciding: null as 'allow' | 'deny' | null,
			errorTitle: '',
			errorMessage: '',
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		codeHalves (): string[] {
			return (this.link?.userCode || '').split('-');
		},
		codeRule (): (value: string) => boolean | string {
			return (value: string): boolean | string => !value || AGENT_CODE_PATTERN.test(normalizeAgentCode(value)) || 'Eight letters and numbers, like ABCD-EFGH';
		},
	},
	mounted () {
		const code: string = normalizeAgentCode(String(this.$route.query.code || ''));
		if (AGENT_CODE_PATTERN.test(code)) this.lookUp(code);
		else this.state = 'enter';
	},
	methods: {
		fail (failure: AgentLinkFailure) {
			forgetWebClientDeviceIfRejected(failure.code);
			if (failure.code === 'DEVICE_CODE_NOT_FOUND') {
				this.errorTitle = 'That code isn\'t waiting';
				this.errorMessage = 'It may have expired (codes last 10 minutes), or been used already. Run login again in your terminal.';
			} else if (failure.code === 'FEEDBACK_AGENT_NOT_ALLOWED') {
				this.errorTitle = 'Staff only';
				this.errorMessage = 'Feedback agents are for RunDiffusion staff signed in with a RunDiffusion email.';
			} else {
				this.errorTitle = 'Something went wrong';
				this.errorMessage = failure.message;
			}
			this.state = 'error';
		},
		async credentials () {
			const credentials = await internalApiCredentials();
			if (!credentials) this.fail({ ok: false, status: 401, code: 'SIGNED_OUT', message: 'You appear to be signed out. Refresh and try again.' });
			return credentials;
		},
		async lookUp (raw: string) {
			const userCode: string = normalizeAgentCode(raw);
			if (!AGENT_CODE_PATTERN.test(userCode)) return;
			this.state = 'loading';
			const credentials = await this.credentials();
			if (!credentials) return;
			const result = await fetchAgentLink({ baseUrl: FEEDBACK_AGENTS_API_BASE_URL, ...credentials, userCode });
			const failure: AgentLinkFailure | null = agentLinkFailure(result);
			if (failure) return this.fail(failure);
			this.link = (result as { ok: true, value: AgentLinkRequest }).value;
			this.state = 'ready';
		},
		async decide (decision: 'allow' | 'deny') {
			if (!this.link || this.state === 'deciding') return;
			this.state = 'deciding';
			this.deciding = decision;
			const credentials = await this.credentials();
			if (!credentials) return;
			const result = await decideAgentLink({ baseUrl: FEEDBACK_AGENTS_API_BASE_URL, ...credentials, userCode: this.link.userCode, decision });
			this.deciding = null;
			const failure: AgentLinkFailure | null = agentLinkFailure(result);
			if (failure) return this.fail(failure);
			this.state = decision === 'allow' ? 'allowed' : 'denied';
		},
		startOver () {
			this.link = null;
			this.typed = '';
			this.state = 'enter';
		},
	},
});
